"use client";
import { useEffect, useRef, useState } from "react";

type Options = { threshold?: number }; // px reduction to treat as "keyboard open"

function getViewportHeight() {
    if (typeof window === "undefined") return 0;
    return window.visualViewport?.height ?? window.innerHeight;
}

export function useKeyboardOpen({ threshold = 120 }: Options = {}) {
    const [isOpen, setIsOpen] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);

    const baseline = useRef<number | null>(null);
    const hasFocus = useRef(false);
    const raf = useRef<number | null>(null);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const vv = window.visualViewport;
        const vk: any = (navigator as any).virtualKeyboard; // Chrome Android (experimental)

        const setBaseline = () => {
            baseline.current = getViewportHeight();
        };

        const updateFromViewport = () => {
            const base = baseline.current ?? getViewportHeight();
            const now = getViewportHeight();
            const delta = Math.max(0, base - now); // how much the viewport shrank
            const open = hasFocus.current && delta > threshold;
            setIsOpen(open);
            setKeyboardHeight(open ? delta : 0);
        };

        const onFocusIn = (e: Event) => {
            const t = e.target as HTMLElement | null;
            hasFocus.current =
                !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable === true);
            // capture baseline just before the keyboard animates
            if (hasFocus.current) setBaseline();
        };

        const onFocusOut = () => {
            hasFocus.current = false;
            // allow the closing animation to finish
            raf.current && cancelAnimationFrame(raf.current);
            raf.current = requestAnimationFrame(() => {
                setIsOpen(false);
                setKeyboardHeight(0);
                setBaseline();
            });
        };

        const onResizeLike = () => {
            raf.current && cancelAnimationFrame(raf.current);
            raf.current = requestAnimationFrame(updateFromViewport);
        };

        // VisualViewport + window fallbacks
        window.addEventListener("focusin", onFocusIn, true);
        window.addEventListener("focusout", onFocusOut, true);
        window.addEventListener("resize", onResizeLike);
        window.addEventListener("orientationchange", setBaseline);
        vv?.addEventListener("resize", onResizeLike);
        vv?.addEventListener("scroll", onResizeLike); // iOS moves vv during keyboard

        // Chrome Android: VirtualKeyboard API (more precise height)
        const onGeometry = () => {
            if (!hasFocus.current || !vk?.boundingRect) return;
            const kb = Math.max(0, window.innerHeight - vk.boundingRect.top);
            const open = kb > threshold;
            setIsOpen(open);
            setKeyboardHeight(open ? kb : 0);
        };
        vk?.addEventListener?.("geometrychange", onGeometry);

        // init baseline once mounted
        setBaseline();

        return () => {
            window.removeEventListener("focusin", onFocusIn, true);
            window.removeEventListener("focusout", onFocusOut, true);
            window.removeEventListener("resize", onResizeLike);
            window.removeEventListener("orientationchange", setBaseline);
            vv?.removeEventListener("resize", onResizeLike);
            vv?.removeEventListener("scroll", onResizeLike);
            vk?.removeEventListener?.("geometrychange", onGeometry);
            if (raf.current) cancelAnimationFrame(raf.current);
        };
    }, [threshold]);

    return { isOpen, keyboardHeight };
}
